import classNames from "classnames";
import { pluralize } from "@common/helpers/string";
import { Amount } from "@design-system/Amount/Amount";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { Separator } from "@design-system/Separator/Separator";
import { TriangleIcon } from "@icons";
import {
    ScheduleSelectionMonth,
    ScheduleSelectionTotals,
} from "./scheduleApplicationSelectionTotals";
import styles from "./ScheduleApplicationsTotals.module.scss";

interface ScheduleApplicationsTotalsProps {
    totals: ScheduleSelectionTotals;
    remainingAsOfLabel: string | null;
    months: ScheduleSelectionMonth[];
}

function dropMonthsBeforeFirstSchedule(
    months: ScheduleSelectionMonth[],
): ScheduleSelectionMonth[] {
    const firstActiveIndex = months.findIndex(
        (month) => month.additions !== 0 || month.remainingAmount !== 0,
    );
    return firstActiveIndex < 0 ? [] : months.slice(firstActiveIndex);
}

export const ScheduleApplicationsTotals = ({
    totals,
    remainingAsOfLabel,
    months,
}: ScheduleApplicationsTotalsProps) => {
    const visibleMonths = dropMonthsBeforeFirstSchedule(months);
    return (
        <Popover>
            <PopoverTrigger openOnHover delay={150}>
                <div className={styles.triggerButton}>
                    <span className="d-block medium text-gray-700">
                        Total: <Amount amount={totals.amount} />
                    </span>
                    <TriangleIcon />
                </div>
            </PopoverTrigger>
            <PopoverContent className={styles.popover}>
                <div className={classNames(styles.line, styles.smallPadding)}>
                    <span>Amount</span>
                    <Amount amount={totals.amount} />
                </div>
                <div className={styles.line}>
                    <span>
                        Remaining balance
                        {remainingAsOfLabel ? ` at ${remainingAsOfLabel}` : ""}
                    </span>
                    <Amount amount={totals.remainingAmount} />
                </div>
                <Separator />
                <div className={styles.line}>
                    <span>{pluralize("Schedule", totals.count)}</span>
                    <span>{totals.count}</span>
                </div>
                {visibleMonths.length > 0 && (
                    <>
                        <Separator />
                        <div className={styles.monthsHeading}>
                            Remaining balance by month end
                        </div>
                        <div className={styles.months}>
                            {visibleMonths.map((month) => (
                                <div
                                    key={month.monthKey}
                                    className={styles.month}
                                >
                                    <span>{month.label}</span>
                                    <Amount amount={month.remainingAmount} />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </PopoverContent>
        </Popover>
    );
};
